import { AlertTriangle, Loader2 } from 'lucide-react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import type { CryptoOnchainStoreStatus } from '@/lib/api/crypto-onchain';
import { formatDate } from '@/lib/format';

const EMPTY_VALUE = '—';

type Props = {
  status: CryptoOnchainStoreStatus | undefined;
  isLoading: boolean;
  isError: boolean;
  /** A mutation or the surrounding view is busy: the enable button waits. */
  isPending: boolean;
};

function getStatusBadge(
  status: CryptoOnchainStoreStatus['status'],
  t: TFunction<'integrations'>,
): { variant: BadgeVariant; label: string } {
  switch (status) {
    case 'active':
      return { variant: 'green', label: t('cryptoOnchain.status.connected') };
    case 'provisioning':
      return { variant: 'yellow', label: t('cryptoOnchain.status.provisioning') };
    case 'disabled':
      return { variant: 'gray', label: t('cryptoOnchain.status.disabled') };
    case 'failed':
      return { variant: 'red', label: t('cryptoOnchain.status.failed') };
    case 'not_enabled':
      return { variant: 'outline', label: t('cryptoOnchain.status.notEnabled') };
    default:
      // A status this build does not know yet (the server may add transitional ones): show it raw.
      return { variant: 'secondary', label: status };
  }
}

function getStatusHint(status: CryptoOnchainStoreStatus['status'], t: TFunction<'integrations'>): string {
  switch (status) {
    case 'active':
      return t('cryptoOnchain.status.connectedHint');
    case 'provisioning':
      return t('cryptoOnchain.status.provisioningHint');
    case 'disabled':
      return t('cryptoOnchain.status.disabledHint');
    case 'failed':
      return t('cryptoOnchain.status.failedHint');
    default:
      return t('cryptoOnchain.status.notEnabledHint');
  }
}

/**
 * The submit button's label while the link is not active. A `failed` (or stuck `provisioning`) link
 * is retried: the server reuses the row and its webhook secret, so this is the same `enable` call.
 */
function getEnableLabel(status: CryptoOnchainStoreStatus['status'], t: TFunction<'integrations'>): string {
  switch (status) {
    case 'disabled':
      return t('cryptoOnchain.reenableButton');
    case 'failed':
    case 'provisioning':
      return t('cryptoOnchain.retryEnableButton');
    default:
      return t('cryptoOnchain.enableButton');
  }
}

/**
 * Link status, crypto-api merchant id, enable date and the platform fee (read-only), plus the enable
 * button while the store is not live. The button submits the surrounding form (`type="submit"`), so
 * enabling always runs the payout-address validation first. A failed provisioning shows the reason
 * the server kept (`lastError`) so the merchant knows what to fix before retrying.
 */
export function CryptoOnchainStatusCard({ status, isLoading, isError, isPending }: Props) {
  const { t } = useTranslation('integrations');

  if (isLoading) return <Skeleton className="h-40 w-full" />;

  if (isError || !status) {
    return (
      <Alert variant="warning">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>{t('cryptoOnchain.status.platformUnavailableTitle')}</AlertTitle>
        <AlertDescription>{t('cryptoOnchain.status.platformUnavailableDescription')}</AlertDescription>
      </Alert>
    );
  }

  const badge = getStatusBadge(status.status, t);
  const hint = getStatusHint(status.status, t);
  const isActive = status.status === 'active';
  const hasFailure = status.status === 'failed' && status.lastError !== null;
  const platformFee =
    status.effectiveSharePercent === null
      ? t('cryptoOnchain.status.platformFeeUnknown')
      : t('cryptoOnchain.status.platformFeeValue', { percent: status.effectiveSharePercent });
  const enableLabel = getEnableLabel(status.status, t);
  const enabledAt = status.enabledAt ? formatDate(status.enabledAt) : EMPTY_VALUE;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <CardTitle className="text-base">{t('cryptoOnchain.status.title')}</CardTitle>
            <CardDescription>{hint}</CardDescription>
          </div>
          <Badge variant={badge.variant}>{badge.label}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {!status.platformAvailable && (
          <Alert variant="warning">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{t('cryptoOnchain.status.platformUnavailableTitle')}</AlertTitle>
            <AlertDescription>{t('cryptoOnchain.status.platformUnavailableDescription')}</AlertDescription>
          </Alert>
        )}

        {hasFailure && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{t('cryptoOnchain.status.lastErrorTitle')}</AlertTitle>
            <AlertDescription className="break-words font-mono text-xs">{status.lastError}</AlertDescription>
          </Alert>
        )}

        <dl className="grid gap-4 text-sm sm:grid-cols-3">
          <div className="space-y-1">
            <dt className="text-muted-foreground">{t('cryptoOnchain.status.merchantId')}</dt>
            <dd>
              <code className="text-xs">{status.cryptoApiMerchantId ?? EMPTY_VALUE}</code>
            </dd>
          </div>
          <div className="space-y-1">
            <dt className="text-muted-foreground">{t('cryptoOnchain.status.enabledAt')}</dt>
            <dd>{enabledAt}</dd>
          </div>
          <div className="space-y-1">
            <dt className="text-muted-foreground">{t('cryptoOnchain.status.platformFee')}</dt>
            <dd className="font-medium">{platformFee}</dd>
            <p className="text-muted-foreground text-xs">{t('cryptoOnchain.status.platformFeeHint')}</p>
          </div>
        </dl>

        {!isActive && (
          <Button type="submit" disabled={!status.platformAvailable || isPending}>
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {enableLabel}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
